// Country-data contract fixture (US-879 · R-4) — registered through the
// US-883 fixtures mechanism (fixtures/apiFixtures.js).
//
// Mirrors the 202 body of POST /country-data-range/ — today's reference
// contract for country data (backend/map_stats/views.py CountryDataRange,
// async pointers per sdd/specs/US-891/contract.md) — for the sections the
// Analysis panel consumes. The real payload carries more sections
// (additionalProperties in the contract); the panel must tolerate extras and
// absences either way, so the fixture stays scoped to what it reads.
//
// All values are synthetic round numbers — representative, NOT production
// data (AGENTS.md §6/§8). Coverage percentages are 0..1 fractions, exactly
// as the backend derives them (utilities/api_util_noengine.py).
import { registerFixture } from "./apiFixtures";
import { ApiError } from "../api/errors";

// optimal_tech_mix keys the synthetic engine stub emits (US-917 spec §1) —
// the guaranteed shape. Round numbers so tests can assert exact strings.
const STUB_TECH_MIX = {
  total_fiber_cost_low_urban: 1_000_000_000,
  total_fiber_cost_high_urban: 1_500_000_000,
  total_fiber_cost_low_rural: 800_000_000,
  total_fiber_cost_high_rural: 1_200_000_000,
  total_fwa_cost_low_urban: 400_000_000,
  total_fwa_cost_high_urban: 600_000_000,
  total_fwa_cost_low_rural: 300_000_000,
  total_fwa_cost_high_rural: 500_000_000,
  total_satellite_cost_low: 100_000_000,
  total_satellite_cost_high: 200_000_000,
  total_capex_low: 2_600_000_000,
  total_capex_high: 4_000_000_000,
  total_hh_covered_fiber_low_urban: 5_000_000,
  total_hh_covered_fiber_high_urban: 7_000_000,
  total_hh_covered_fiber_low_rural: 2_000_000,
  total_hh_covered_fiber_high_rural: 3_000_000,
};

// Keys only the REAL engine emits (unsuffixed aggregates, FWA/satellite HH,
// build-out density) — optional for the panel (R-4). PHL carries them so the
// full-shape path is exercisable; IDN stays stub-only for the degraded path.
const ENGINE_ONLY_TECH_MIX = {
  total_fiber_cost_low: 1_800_000_000,
  total_fiber_cost_high: 2_700_000_000,
  total_fwa_cost_low: 700_000_000,
  total_fwa_cost_high: 1_100_000_000,
  total_hh_covered_fiber_low: 7_000_000,
  total_hh_covered_fiber_high: 10_000_000,
  total_hh_covered_fwa_urban: 1_500_000,
  total_hh_covered_fwa_rural: 2_500_000,
  total_hh_covered_satellite: 500_000,
  build_fiber_to_popdensity_high: 300,
};

// One synthetic scorecard, parameterized so the two sample countries differ
// visibly in the panel (population scale + coverage profile).
const scorecard = ({ scale, urbanShare, coverage4G, fixedUpper }) => {
  const total = 100_000_000 * scale;
  const urban = Math.round(total * urbanShare);
  const rural = Math.round(total * (1 - urbanShare) * 0.8);
  const extremeRural = total - urban - rural;
  const hh = 4.0;
  return {
    pop_data: {
      rural,
      urban,
      extreme_rural: extremeRural,
      downto300: Math.round(urban * 0.9),
    },
    household_data: {
      rural: rural / hh,
      urban: urban / hh,
      extreme_rural: extremeRural / hh,
      downto300: Math.round(urban * 0.9) / hh,
      total: total / hh,
    },
    coverage: {
      "4G": {
        perc_urbanpop: Math.min(coverage4G + 0.2, 0.99),
        perc_ruralpop: coverage4G,
        perc_extreme_ruralpop: Math.max(coverage4G - 0.3, 0.05),
      },
      fixed: {
        perc_urbanHH_upper: fixedUpper,
        perc_urbanHH_lower: fixedUpper - 0.1,
        perc_ruralHH_upper: fixedUpper - 0.35,
        perc_ruralHH_lower: fixedUpper - 0.45,
        perc_extreme_ruralHH_upper: 0.1,
        perc_extreme_ruralHH_lower: 0.05,
        perc_downto300_fixed_upper: fixedUpper,
        perc_downto300_fixed_lower: fixedUpper - 0.1,
      },
      satellite: {
        perc_ruralHH: coverage4G,
        perc_extremeruralHH: Math.max(coverage4G - 0.3, 0.05),
      },
    },
    total_capex: {
      cost_lower: 400_000_000 * scale,
      cost_upper: 650_000_000 * scale,
    },
    demographics: {
      wb_population: total,
      un_household_size: hh,
      // Country TOTAL, like the real payload (per capita is derived client-side).
      wb_GNI: (3500 + 2500 * scale) * total,
      wb_decile1: 1.9,
      wb_decile2: 2.9,
      wb_decile3: 3.9,
      wb_decile4: 5.0,
      wb_decile5: 6.2,
      wb_decile6: 7.7,
      wb_decile7: 9.6,
      wb_decile8: 12.4,
      wb_decile9: 17.1,
      wb_decile10: 33.3,
    },
  };
};

// The two sample countries (spec design.md §2): a large archipelago profile
// and a smaller lower-coverage profile. Any other DMC falls through to the
// backend's own 400 shape below. PHL carries the full engine tech-mix shape,
// IDN only the stub keys — both R-4 paths exercisable (US-917 · R-8).
export const COUNTRY_FIXTURES = {
  PHL: {
    ...scorecard({ scale: 1.1, urbanShare: 0.48, coverage4G: 0.6, fixedUpper: 0.75 }),
    optimal_tech_mix: { ...STUB_TECH_MIX, ...ENGINE_ONLY_TECH_MIX },
  },
  IDN: {
    ...scorecard({ scale: 2.7, urbanShare: 0.57, coverage4G: 0.5, fixedUpper: 0.65 }),
    optimal_tech_mix: { ...STUB_TECH_MIX },
  },
};

registerFixture("countryDataRange", (body = {}) => {
  const fixture = COUNTRY_FIXTURES[body.country_name];
  if (!fixture) {
    // Mirror the backend's unknown-country shape (views.py: 400 {error}) so
    // the panel's error state is exercisable without a backend.
    throw new ApiError({
      status: 400,
      message: "Request to /country-data-range/ failed with status 400",
      detail: "Invalid country ISO value",
      path: "/country-data-range/",
    });
  }
  return {
    ...fixture,
    // Async report pointers guaranteed by the 202 contract (US-891). The
    // Analysis panel ignores them; US-882's useReportJob is their consumer.
    task_id: "00000000-0000-4000-8000-000000000879",
    status: "processing",
    status_url: "/reports/00000000-0000-4000-8000-000000000879/status/",
  };
});
